import { AutomobileAuction } from '@/reports/types/vehicles';
import {
  Box,
  Divider,
  Icons,
  Modal,
  Table,
  Pill,
  PureTextButton,
  Stack,
  Text,
  styled,
} from '@/theme/index';
import { capitalizeString } from '@/utils/capitalizeString';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { commaFormat, commaFormatCurrency } from '@/utils/index';

const StyledPill = styled(Pill)(({ theme }) => ({
  borderRadius: '0.25rem',
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.primary.main,
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  marginInline: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(2, 0),
  },
}));

export const StyledModal = styled(Modal)(({ theme }) => ({
  '.MuiDialog-paper': {
    borderRadius: '0.5rem',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  tr: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  td: {
    '&:last-of-type': {
      textAlign: 'right',
    },
  },
}));

export const CardAuctionDetails = ({
  auction,
  handleClickOnSeeDetailsClose,
}: {
  auction: AutomobileAuction;
  handleClickOnSeeDetailsClose: () => void;
}): JSX.Element => {
  const { hasLocation, location, hasDate, dateFormatted } =
    generateDetails(auction);

  return (
    <Stack direction="column" width={{ sm: '100%', md: '50%' }} spacing={0}>
      {Boolean(auction.high_bid) && (
        <Text variant="h3" mb={1}>
          Current Bid: {commaFormatCurrency(auction.high_bid)}
        </Text>
      )}
      {Boolean(auction.est_retail_value) && (
        <Text variant="body2" fontSize="0.75rem">
          Est Retail Value: {commaFormatCurrency(auction.est_retail_value)}
        </Text>
      )}
      {(hasLocation || hasDate) && (
        <Text variant="body2" fontSize="0.75rem">
          {[location, dateFormatted].filter(Boolean).join(' | ')}
        </Text>
      )}
      {Boolean(auction.vehicle_condition_details?.odometer) && (
        <Text marginTop={1.25}>
          <Icons.Star
            color="primary"
            fontSize="small"
            sx={{ verticalAlign: 'middle', mt: -0.625, mr: 1 }}
          />
          <Text component="span" fontWeight="bold" mr={0.625}>
            {commaFormat(auction.vehicle_condition_details?.odometer)} Miles
          </Text>
          <Text component="span">(Actual)</Text>
        </Text>
      )}
      <PureTextButton
        variant="text"
        onClick={() => handleClickOnSeeDetailsClose()}
        sx={{ pl: 0, pb: 0, fontSize: '0.75rem' }}
        color="success"
      >
        See details
      </PureTextButton>
    </Stack>
  );
};

export const CardTitleDetails = ({
  auction,
}: {
  auction: AutomobileAuction;
}): JSX.Element => {
  const { hasTitle, title } = generateDetails(auction);

  return (
    <Stack direction="column" width={{ sm: '100%', md: '50%' }} spacing={1}>
      {hasTitle && (
        <Text variant="h3" color={title.color} mb={0}>
          {title.icon && (
            <Icons.Warning
              color="error"
              fontSize="small"
              sx={{ verticalAlign: 'middle', mt: -0.625, mr: 1 }}
            />
          )}
          {title.text}
        </Text>
      )}
      {auction.vehicle_condition_details?.damage_description && (
        <Text variant="body1">
          <Text variant="body1" component="span" fontWeight="bold">
            Damage:
          </Text>{' '}
          {auction.vehicle_condition_details?.damage_description}
        </Text>
      )}
      {auction.vehicle_condition_details?.secondary_damage && (
        <Text variant="body1">
          <Text variant="body1" component="span" fontWeight="bold">
            Secondary:
          </Text>{' '}
          {auction.vehicle_condition_details?.secondary_damage}
        </Text>
      )}
      {TitlePills({ auction })}
    </Stack>
  );
};

export const ModalContent = ({
  auction,
  vehicleYMM,
}: {
  auction: AutomobileAuction;
  vehicleYMM: string;
}): JSX.Element => {
  const { hasLocation, location, hasDate, dateFormatted, title } =
    generateDetails(auction);
  const conditionRows = [
    ['Title:', title.text],
    ['Damage:', auction.vehicle_condition_details?.damage_description || ''],
    ['Secondary:', auction.vehicle_condition_details?.secondary_damage || ''],
    ['Odometer:', commaFormat(auction.vehicle_condition_details?.odometer)],
    [
      'Key:',
      auction.vehicle_condition_details?.has_keys
        ? 'Available'
        : 'Not Available',
    ],
  ];

  const saleRows = [
    ['Location:', location],
    [
      'Lane/GridRow',
      [auction.lot_cond_code, auction.sale?.grid_row]
        .filter(Boolean)
        .join(' / '),
    ],
    ['Item#:', auction.item_number || '-'],
  ];

  return (
    <Box
      max-height="400px"
      height="400px"
      max-width={{ sm: '100%', md: '500px' }}
      width={{ sm: '100%', md: '500px' }}
      overflow="hidden"
    >
      <Text variant="h5" mb={0}>
        {vehicleYMM}
      </Text>
      {(hasLocation || hasDate) && (
        <Text variant="body2">
          {[location, dateFormatted].filter(Boolean).join(' | ')}
        </Text>
      )}
      {TitlePills({ auction })}
      <Box mt={3} height="270px" overflow="scroll">
        {Boolean(auction.high_bid) && (
          <Text variant="h4" mb={0.5}>
            Current Bid: {commaFormatCurrency(auction.high_bid)}
          </Text>
        )}
        {Boolean(auction.est_retail_value) && (
          <Text variant="body2">
            Est Retail Value: {commaFormatCurrency(auction.est_retail_value)}
          </Text>
        )}
        <Text variant="h5" marginY={1}>
          Condition
        </Text>
        <StyledTable rows={conditionRows} />
        <Text variant="h5" marginY={1}>
          Sale Info
        </Text>
        <StyledTable rows={saleRows} />
      </Box>
    </Box>
  );
};

const TitlePills = ({
  auction,
}: {
  auction: AutomobileAuction;
}): JSX.Element => {
  return (
    <Stack direction="row" spacing={1} mt={2}>
      {auction.vehicle_condition_details?.has_keys && (
        <StyledPill label="Has keys" size="small" />
      )}
      {auction.runs_drives && (
        <StyledPill label={auction.runs_drives} size="small" />
      )}
    </Stack>
  );
};

function generateDetails(auction: AutomobileAuction) {
  const city = capitalizeString(auction.sale?.address?.parsed?.city || '');
  const state = auction.sale?.address?.parsed?.state?.toUpperCase();
  const title = {
    text: '',
    color: 'common.black',
    type: auction.sale?.title_type,
    icon: auction.sale?.title_type === 'SV',
  };
  if (auction.sale?.title_type === 'CT') {
    title.text = `Clean Title - ${auction.sale?.title_state?.toUpperCase()}`;
    title.color = 'success';
  } else if (auction.sale?.title_type === 'SV') {
    title.text = `Salvage Title - ${auction.sale?.title_state?.toUpperCase()}`;
    title.color = 'error';
  }

  return {
    hasTitle:
      Boolean(auction.sale?.title_type) && Boolean(auction.sale?.title_state),
    title,
    city,
    state,
    hasLocation: Boolean(city) && Boolean(state),
    location: [state, city].filter(Boolean).join(' - '),
    hasDate: Boolean(auction.sale?.date?.full),
    dateFormatted: formatDateFromReport(auction.sale?.date?.full),
  };
}
